/**
 * Meten in de werkplek (sessie 72, blok 1; meetplicht B-2026-08-17).
 *
 * Per handeling (versturen, sluiten, zaak openen, nieuw bericht in de lijst)
 * de tijd van begin tot het frame waarin het scherm het toont, met een
 * uitsplitsing in vaste stappen. Het gaat op twee plekken heen:
 *
 * - `performance.measure("v6:<actie>")`, zichtbaar in de devtools;
 * - fire-and-forget naar `/api/ui-metingen` als `ui_actie_duur`, zodat de
 *   meting op productie doorloopt. Alleen de handeling en getallen: geen
 *   ids, geen inhoud; de server bewaart alleen de apparaatklasse.
 *
 * Het begin van een handeling op de server (versturen, sluiten) halen we uit
 * `PerformanceResourceTiming` van de aanvraag zelf: dan hoeft de knop niets
 * te weten van meten.
 */
import { apiClient } from "@/lib/api";

export type UiActie = "versturen" | "sluiten" | "zaak_openen" | "nieuw_bericht";
export type UiStap = "request" | "lijst" | "zaak" | "render" | "live";
export type Stappen = Partial<Record<UiStap, number>>;

const PAD = "/api/ui-metingen";

/** Een frame verder: dan staat de wijziging op het scherm. */
function naFrame(): Promise<number> {
  return new Promise((klaar) => {
    if (typeof requestAnimationFrame !== "function") {
      klaar(performance.now());
      return;
    }
    requestAnimationFrame(() => klaar(performance.now()));
  });
}

function rond(ms: number): number {
  return Math.max(0, Math.round(ms));
}

/** Verstuur de meting; faalt nooit en blokkeert nooit. */
export function meld(actie: UiActie, duurMs: number, stappen: Stappen): void {
  try {
    if (import.meta.env.MODE === "test") return;
    const schoon: Stappen = {};
    for (const [k, v] of Object.entries(stappen)) {
      if (typeof v === "number" && Number.isFinite(v)) schoon[k as UiStap] = rond(v);
    }
    void apiClient
      .post(PAD, { gebeurtenis: "ui_actie_duur", actie, duur_ms: rond(duurMs), stappen: schoon }, { timeout: 5000 })
      .catch(() => undefined);
  } catch {
    /* meten raakt nooit het scherm */
  }
}

function markeer(actie: UiActie, begin: number, eind: number): void {
  try {
    performance.measure(`v6:${actie}`, { start: begin, end: eind });
  } catch {
    /* oudere browser: alleen de melding */
  }
}

/** Een aanvraag die langer geleden klaar was, hoort niet bij deze handeling. */
export const RECENT_MS = 2_000;
/**
 * De buffer van Resource Timing is standaard 250 regels. Een werkplek die de
 * hele dag openstaat, loopt daar overheen; daarna komen er geen nieuwe
 * aanvragen meer bij. Boven deze grens legen we hem na het lezen.
 */
const BUFFER_LEEG_BOVEN = 200;

/**
 * De laatste aanvraag waarvan het pad op `patroon` past en die net klaar
 * was (binnen `RECENT_MS`); anders null en meten we zonder request-stap.
 */
export function laatsteAanvraag(patroon: RegExp): PerformanceResourceTiming | null {
  try {
    const nu = performance.now();
    const lijst = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    let gevonden: PerformanceResourceTiming | null = null;
    for (let i = lijst.length - 1; i >= 0; i -= 1) {
      const pad = lijst[i].name.split("?")[0];
      if (!patroon.test(pad)) continue;
      if (nu - lijst[i].responseEnd < RECENT_MS) gevonden = lijst[i];
      break;
    }
    if (lijst.length >= BUFFER_LEEG_BOVEN) performance.clearResourceTimings();
    return gevonden;
  } catch {
    /* geen resource timing */
  }
  return null;
}

/**
 * Na een geslaagde handeling (het antwoord is binnen en de cache is
 * bijgewerkt): meet vanaf het begin van de aanvraag tot het volgende frame.
 */
export function meetNaAanvraag(actie: UiActie, patroon: RegExp): void {
  const nu = performance.now();
  const aanvraag = laatsteAanvraag(patroon);
  const begin = aanvraag && aanvraag.startTime <= nu ? aanvraag.startTime : nu;
  const request = aanvraag ? aanvraag.responseEnd - aanvraag.startTime : undefined;
  void naFrame().then((frame) => {
    markeer(actie, begin, frame);
    meld(actie, frame - begin, { request, render: frame - nu });
  });
}

/**
 * Een meting die over renders heen loopt (zaak openen, nieuw bericht):
 * `begin` bij de klik of het event, `klaar` als het scherm het toont.
 */
export interface LopendeMeting {
  actie: UiActie;
  begin: number;
  stappen: Stappen;
}

export function beginMeting(actie: UiActie, begin: number = performance.now()): LopendeMeting {
  return { actie, begin, stappen: {} };
}

export function rondMetingAf(meting: LopendeMeting, extra: Stappen = {}): void {
  const nu = performance.now();
  void naFrame().then((frame) => {
    markeer(meting.actie, meting.begin, frame);
    meld(meting.actie, frame - meting.begin, { ...meting.stappen, ...extra, render: frame - nu });
  });
}

/** Het `op`-veld van een live-event in de klok van de pagina. */
export function inPaginaKlok(iso: string): number | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return ms - performance.timeOrigin;
}
